import type { DashboardAnnualDaySlot } from './dashboardAnnualDaySlots';
import { gatePresentationOpen } from '../components/common/PresentationScreenGate';
import {
  closeProgramContentSlideTabPlaceholder,
  openProgramContentSlideFailureMessage,
  openProgramContentSlideInNewTab,
  openProgramContentSlideInSameWindow,
  openProgramContentSlideTabPlaceholder,
} from './programContentSlideStorage';
import {
  fetchProgramContentSlidePresenceKeySet,
  programContentSlidePresenceKey,
} from '../services/programContentSlideService';
import {
  isPersistableUuid,
  pickProgramContentSlideOwnerWithDbPresence,
} from './programContentSlideOwner';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { markDevPresentationOpenTransferScope } from './devNetworkTransferMonitor';
import {
  applyProgramContentSlideLearningOverlayToSlot,
  resolveDashboardSlotPresentationOpenFallbackOwner,
} from './programContentSlideLearningOverlay';

export type OpenDashboardAnnualSlotPresentationParams = {
  slot: DashboardAnnualDaySlot;
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  /** 학기 강제 지정(없으면 슬롯 termNumber) */
  termNumber?: 1 | 2 | null;
  /** true면 새 창/탭 대신 지금 창에서 연다(메인 프레젠) */
  sameWindow?: boolean;
};

/**
 * 연간진도·메인 프레젠과 동일하게 해당 수업 슬롯의 프레젠테이션 탭을 연다.
 */
export function openDashboardAnnualSlotPresentation(
  params: OpenDashboardAnnualSlotPresentationParams
): void {
  const run = (fn: () => void) => (params.sameWindow ? fn() : gatePresentationOpen(fn));
  run(() => {
  const { slot: rawSlot, workspaceId, gradeId, academicYear } = params;
  const slot = applyProgramContentSlideLearningOverlayToSlot(rawSlot, workspaceId);
  const termNumber = (params.termNumber ?? slot.termNumber ?? 1) as 1 | 2;
  const candidates = (slot.slideOwnerCandidates ?? []).filter((o) => isPersistableUuid(o.id));
  const fallbackOwner = resolveDashboardSlotPresentationOpenFallbackOwner(slot);
  const canCreateOnSave = !!slot.slideCreatePresentationRefOnSave;
  const slideTab = params.sameWindow ? null : openProgramContentSlideTabPlaceholder();
  markDevPresentationOpenTransferScope();
  void (async () => {
    let owner: ProgramContentSlideOwnerRef | null = fallbackOwner;
    try {
      const presenceKeys = await fetchProgramContentSlidePresenceKeySet(workspaceId, candidates, {
        cacheOnly: true,
      });
      owner =
        pickProgramContentSlideOwnerWithDbPresence(
          candidates,
          presenceKeys,
          programContentSlidePresenceKey,
          fallbackOwner
        ) ?? fallbackOwner;
    } catch {
      owner = fallbackOwner;
    }
    const learningRaw = (slot.slideLearningTextRaw ?? '').trim();
    const createOnSave =
      owner || (canCreateOnSave && learningRaw)
        ? slot.slideCreatePresentationRefOnSave
        : undefined;
    const d = slot.display;
    const slidePayload: Parameters<typeof openProgramContentSlideInNewTab>[0] = {
        v: 1,
        workspaceId,
        academicYear,
        termNumber,
        annualCellKey: (slot.annualCellKey ?? '').trim() || undefined,
        contextGradeId: gradeId || undefined,
        scopeId: slot.slideWorkspaceScopeId.trim() || undefined,
        owner,
        learningTextOwner: slot.slideOpenDbOwner ?? slot.slideOwner,
        bindingLearningText: slot.slideLearningTextRaw,
        createPresentationRefOnSave: createOnSave,
        extraSlideBodies: [''],
        meta: {
          templateLabel: (slot.templateLabel ?? '').trim(),
          unit: (d.progressUnit ?? '').trim(),
          page: (d.progressPage ?? '').trim(),
          assistantPage: (d.progressAssistantPage ?? '').trim(),
          programContent: (d.progressLearning ?? '').trim(),
          achievementText: (d.progressUnitNote ?? '').trim(),
          learningMemoNote: (d.progressLearningNote ?? '').trim(),
          evaluationMemoNote: (d.progressEvalNote ?? '').trim(),
        },
      };
    const opened = params.sameWindow
      ? openProgramContentSlideInSameWindow(slidePayload)
      : openProgramContentSlideInNewTab(slidePayload, { preOpenedWindow: slideTab ?? undefined });
    if (!opened.ok) {
      closeProgramContentSlideTabPlaceholder(slideTab);
      window.alert(openProgramContentSlideFailureMessage(opened.reason));
    }
  })();
});
}
